import { formatDate, type LeaderReportDto } from '@nova/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../components/Button.js';
import { EmptyState, ErrorState } from '../components/EmptyState.js';
import { GlassPanel } from '../components/GlassPanel.js';
import { MarkdownView } from '../components/RichTextEditor.js';
import { SkeletonPanel } from '../components/Skeleton.js';
import { api } from '../lib/api.js';

/**
 * The private, superadmin-only leadership report. The nav item and the API are
 * both gated on superadmin, so a non-superadmin never sees it or its data.
 */
export function LeaderPage() {
  const {
    data: reports = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['leader-reports'],
    queryFn: () => api.get<LeaderReportDto[]>('/leader-reports'),
  });

  const [openId, setOpenId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const generate = useMutation({
    mutationFn: () => api.post<{ report: LeaderReportDto | null }>('/leader-reports/generate', {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['leader-reports'] }),
  });

  if (isError) {
    return (
      <GlassPanel>
        <ErrorState onRetry={() => void refetch()} />
      </GlassPanel>
    );
  }

  const [latest, ...older] = reports;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-ink flex items-center gap-2 text-xl">
            <Lock size={18} aria-hidden /> Ledelse
          </h1>
          <p className="text-ink-soft mt-0.5 text-sm">
            Privat ukesoppsummering — kun synlig for superadmin. Til oppfølging og ros.
          </p>
        </div>
        <Button
          size="sm"
          icon={<Sparkles size={14} aria-hidden />}
          loading={generate.isPending}
          onClick={() => generate.mutate()}
        >
          {generate.isPending ? 'Nova skriver …' : 'Generer nå'}
        </Button>
      </header>

      {generate.isSuccess && !generate.data.report && (
        <p className="text-ink-soft text-xs">
          Ingen aktivitet å oppsummere for perioden ennå.
        </p>
      )}
      {generate.isError && (
        <p className="text-danger text-xs">{(generate.error as Error).message}</p>
      )}

      {isLoading ? (
        <SkeletonPanel lines={5} />
      ) : reports.length === 0 || !latest ? (
        <GlassPanel>
          <EmptyState
            title="Ingen oppsummeringer ennå"
            description="Den første kommer mandag morgen."
          />
        </GlassPanel>
      ) : (
        <>
          <GlassPanel>
            <p className="label-micro">{periodLabel(latest)}</p>
            <h2 className="font-display text-ink mt-1 text-lg">{latest.headline}</h2>
            <div className="mt-3">
              <MarkdownView markdown={latest.body} />
            </div>
          </GlassPanel>

          {older.length > 0 && (
            <GlassPanel>
              <p className="label-micro mb-2">Tidligere</p>
              <ul className="space-y-2">
                {older.map((report) => (
                  <li key={report.id} className="border-hairline bg-base/30 rounded-lg border">
                    <button
                      type="button"
                      onClick={() => setOpenId(openId === report.id ? null : report.id)}
                      className="flex w-full items-center justify-between gap-3 px-3.5 py-2.5 text-left"
                    >
                      <span className="min-w-0">
                        <span className="text-ink block truncate text-sm">{report.headline}</span>
                        <span className="text-ink-soft block text-[11px]">
                          {periodLabel(report)}
                        </span>
                      </span>
                    </button>
                    {openId === report.id && (
                      <div className="border-hairline border-t p-3.5">
                        <MarkdownView markdown={report.body} />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </GlassPanel>
          )}
        </>
      )}
    </div>
  );
}

function periodLabel(report: LeaderReportDto): string {
  return `${formatDate(report.periodStart)} – ${formatDate(report.periodEnd)}`;
}
